import React, { useEffect, useState } from "react";
import {
  Table,
  Form,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { FiFile } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { getNote } from "../../store/slices/notes";
import { getClassSubject } from "../../store/slices/subject";

export default function StudentNotes() {
  const dispatch = useDispatch();

  const activeStudent = useSelector(
    (state) => state.activestudent?.student
  );

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 25;
  const [totalData, setTotalData] = useState(0);

  const [subjectList, setSubjectList] = useState([]);
  const [subjectId, setSubjectId] = useState("");

  const fetchNotes = (params = {}) => {
    setIsLoading(true);

    dispatch(getNote(params))
      .unwrap()
      .then((res) => {
        setData(res?.data || []);
        setTotalData(res?.totalDataCount || 0);
      })
      .catch(() => {
        toast.error("Failed to fetch notes");
        setData([]);
      })
      .finally(() => setIsLoading(false));
  };


  useEffect(() => {
    const standardId = activeStudent?.standardId?._id;
    const sectionId = activeStudent?.sectionId?._id;

    if (!standardId || !sectionId) return;

    // fetch subjects <------------
    dispatch(getClassSubject({ standardId, sectionId }))
      .unwrap()
      .then((res) => {
        const subjects = res?.data?.[0]?.subjectId || [];
        setSubjectList(subjects);
      })
      .catch(() => toast.error("Failed to fetch subjects"));

    // ---- >fetch notes without subject filter
    fetchNotes({ standardId, sectionId });
    setSubjectId("");
    setCurrentPage(1);
  }, [activeStudent?.standardId?._id, activeStudent?.sectionId?._id]);

  // subject change filter <---------
  useEffect(() => {
    const standardId = activeStudent?.standardId?._id;
    const sectionId = activeStudent?.sectionId?._id;

    if (!standardId || !sectionId) return;

    if (!subjectId) {
      fetchNotes({ standardId, sectionId });
    } else {
      fetchNotes({ standardId, sectionId, subjectId });
    }

    setCurrentPage(1);
  }, [subjectId]);

  // Pagination
  const totalPages = Math.ceil(totalData / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = data.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/student/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>


            <FiChevronRight className="separator" />

            <span className="active">Notes</span>
          </div>

        </div>
      </div>

      <div className="staff-card">
        <Row className="align-items-center mb-3">


          <Col md={12} className="d-flex justify-content-end">
            <Form.Select
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              style={{ maxWidth: "250px" }}
            >
              <option value="">All Subjects</option>
              {subjectList.map((sub) => (
                <option key={sub._id} value={sub._id}>
                  {sub.name}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {/* Table */}
        <div className="table-responsive">
          <Table className="staff-table align-middle">
            <thead>
              <tr>

                <th>#</th>
                <th>Standard</th>
                <th>Subject</th>
                <th>Chapter</th>
                <th>File</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    Loading...
                  </td>
                </tr>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item, i) => (
                  <tr key={item._id}>

                    <td>{startIndex + i + 1}</td>
                    <td>{item.standardId?.name}</td>
                    <td className="text-truncate" style={{ maxWidth: "250px" }}>
                      {item.subjectId?.name}
                    </td>
                    <td className="text-truncate" style={{ maxWidth: "250px" }}>
                      {item.chapter}
                    </td>
                    <td>
                      {item.file ? (
                        <a href={item.file} target="_blank" rel="noreferrer">
                          <FiFile />
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    No notes found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <div className="d-flex justify-content-end my-3">
          <Pagination className="mb-0">

            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            />

            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;

              // Show first page, last page, and pages around current page
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              }

              // Left dots
              if (page === currentPage - 2) {
                return <Pagination.Ellipsis key={`left-${page}`} disabled />;
              }

              // Right dots
              if (page === currentPage + 2) {
                return <Pagination.Ellipsis key={`right-${page}`} disabled />;
              }

              return null;
            })}

            <Pagination.Next
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
            />

          </Pagination>
        </div>
      </div>
    </>
  );
}
